import React, { useCallback, useState } from "react";
import { MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import {
  LinkOff as LinkOffIcon,
  ApartmentOutlined,
  SettingsOutlined,
  ViewModuleOutlined,
} from "@mui/icons-material";
import {
  BaseContextMenu,
  BaseContextMenuProps,
} from "../../../../components/datagrid/components/BaseContextMenu";
import { RemoveAsinsFromProduct } from "../../../../components/forms/CreateProductWizard/RemoveAsinsFromProduct";
import { CreateProductWizard } from "../../../../components/forms/CreateProductWizard/CreateProductWizard";
import FamilyManagement from "../../pricetool/contextMenu/menus/FamilyManagementProduct/FamilyManagement";
import { InventoryCombinationsDisplay } from "../../pricetool/contextMenu/menus/ProductInventoryCombinations/InventoryCombinationsDisplay";

const ProductContextMenu: React.FC<BaseContextMenuProps> = ({
  open,
  event,
  closeContextMenu,
  selectedRows,
  onGridRefresh,
  ...rest
}) => {
  const [activeModal, setActiveModal] = useState<string>("");
  const [productWizardConfig, setProductWizardConfig] = useState<{
    initialProduct: any;
    mode: "edit" | "create";
    initialPage: number;
  } | null>(null);

  const openContextModal = useCallback((modalName: string) => {
    setActiveModal(modalName);
  }, []);

  const closeModal = useCallback(() => {
    setActiveModal("");
    setProductWizardConfig(null);
  }, []);

  const closeModalAndRefresh = useCallback(() => {
    setActiveModal("");
    setProductWizardConfig(null);
    if (onGridRefresh) {
      onGridRefresh();
    }
    closeContextMenu();
  }, [onGridRefresh, closeContextMenu]);

  const handleMainMenuClose = useCallback(() => {
    setActiveModal("");
    setProductWizardConfig(null);
    closeContextMenu();
  }, [closeContextMenu]);

  const handleOpenEditFamilies = useCallback(() => {
    if (selectedRows.length === 0) return;

    setActiveModal("family");
  }, [selectedRows, closeContextMenu]);

  const handleOpenEditProduct = useCallback(() => {
    if (selectedRows.length === 0) return;

    const row = selectedRows[0];
    closeContextMenu();
    setProductWizardConfig({
      initialProduct: {
        product_id: row.product.product_id,
        families: (row.product_families ?? []).map((f: any) => ({
          id: f.value,
          title: f.label,
        })),
        title: row.product.product_title || "",
        partNumber: row.product.assembly_part_number || "",
      },
      mode: "edit",
      initialPage: 2,
    });
    setActiveModal("productWizard");
  }, [selectedRows, closeContextMenu]);

  // Resolve which custom modal to show (or null for none)
  let resolvedCustomModal: React.ReactNode | null = null;
  if (activeModal === "removeAsins" && selectedRows.length === 1) {
    const row = selectedRows[0];
    resolvedCustomModal = (
      <RemoveAsinsFromProduct
        productId={row.product.product_id}
        productTitle={row.product.product_title ?? ""}
        onClose={closeModalAndRefresh}
      />
    );
  } else if (activeModal === "family") {
    resolvedCustomModal = (
      <FamilyManagement
        open={true}
        close={closeModalAndRefresh}
        initialProducts={selectedRows}
      />
    );
  } else if (
    activeModal === "inventory-combinations" &&
    selectedRows.length === 1
  ) {
    resolvedCustomModal = (
      <InventoryCombinationsDisplay
        open={true}
        close={closeModal}
        product={selectedRows[0]}
      />
    );
  } else if (activeModal === "productWizard" && productWizardConfig) {
    resolvedCustomModal = (
      <CreateProductWizard
        mode={productWizardConfig.mode}
        initialProduct={productWizardConfig.initialProduct}
        initialPage={productWizardConfig.initialPage}
        onClose={closeModalAndRefresh}
      />
    );
  }

  return (
    <BaseContextMenu
      open={open}
      event={event}
      closeContextMenu={handleMainMenuClose}
      selectedRows={selectedRows}
      onGridRefresh={onGridRefresh}
      activeCustomModal={resolvedCustomModal}
      {...rest}
    >
      <MenuItem onClick={handleOpenEditFamilies}>
        <ListItemIcon>
          <ApartmentOutlined fontSize="small" />
        </ListItemIcon>
        <ListItemText>Edit Families</ListItemText>
      </MenuItem>

      <MenuItem
        disabled={selectedRows.length > 1}
        onClick={handleOpenEditProduct}
      >
        <ListItemIcon>
          <SettingsOutlined fontSize="small" />
        </ListItemIcon>
        <ListItemText>Edit Product Details</ListItemText>
      </MenuItem>

      <MenuItem
        disabled={selectedRows.length > 1}
        onClick={() => openContextModal("inventory-combinations")}
      >
        <ListItemIcon>
          <ViewModuleOutlined fontSize="small" />
        </ListItemIcon>
        <ListItemText>View Inventory Sets</ListItemText>
      </MenuItem>

      <MenuItem
        disabled={selectedRows.length !== 1}
        onClick={() => openContextModal("removeAsins")}
      >
        <ListItemIcon>
          <LinkOffIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Remove ASINs from Product</ListItemText>
      </MenuItem>
    </BaseContextMenu>
  );
};

export default ProductContextMenu;
